import { useEffect, useRef, useState } from "react";
import { avisarError } from "../../shared/errores";
import { cambiarPortada, guardarProyecto, quitarPortada as quitarPortadaDe } from "./proyectosService";
import { SelectorPersona, Ventana } from "./componentes";
import { botonFantasma, botonPrimario, campo, etiqueta, pequeno } from "./estilos";
import { ESTADOS_PROYECTO, PRIORIDADES, reducirImagen } from "./reglas";
import Selector from "../../shared/components/Selector";

const VACIO = {
  nombre: "", descripcion: "", ministerio: "", lider_id: "",
  fecha_inicio: "", fecha_fin: "", estado: "planeado", prioridad: "media",
};

// Crear o editar un proyecto, con su imagen de portada. El líder solo lo cambia un
// administrador (lo valida también el backend).
function FormProyecto({ proyecto, personas, puedeCambiarLider, onGuardado, onCerrar }) {
  const [form, setForm] = useState(() => {
    const inicial = { ...VACIO };
    Object.keys(VACIO).forEach((c) => { if (proyecto?.[c] != null) inicial[c] = proyecto[c]; });
    return inicial;
  });
  // Portada: la nueva se sube después de guardar el proyecto (así no quedan imágenes sueltas).
  const [portadaNueva, setPortadaNueva] = useState(null);
  const [vistaPrevia, setVistaPrevia] = useState(proyecto?.portada_url || "");
  const [quitarPortada, setQuitarPortada] = useState(false);
  const [preparando, setPreparando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const selector = useRef(null);
  const cambiar = (c) => (e) => setForm({ ...form, [c]: e.target.value });

  useEffect(() => () => { if (vistaPrevia.startsWith("blob:")) URL.revokeObjectURL(vistaPrevia); }, [vistaPrevia]);

  const elegirImagen = async (archivo) => {
    if (!archivo) return;
    if (!archivo.type.startsWith("image/")) return avisarError(new Error("El archivo debe ser una imagen."));
    setPreparando(true);
    try {
      const reducida = await reducirImagen(archivo);
      setPortadaNueva(reducida);
      setVistaPrevia(URL.createObjectURL(reducida));
      setQuitarPortada(false);
    } catch (error) {
      avisarError(error);
    } finally {
      setPreparando(false);
    }
  };

  const quitar = () => {
    setPortadaNueva(null);
    setVistaPrevia("");
    setQuitarPortada(Boolean(proyecto?.portada_url));
  };

  const guardar = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) return avisarError(new Error("El nombre del proyecto es obligatorio."));
    if (form.fecha_inicio && form.fecha_fin && form.fecha_fin < form.fecha_inicio) {
      return avisarError(new Error("La fecha de fin no puede ser anterior a la de inicio."));
    }
    setGuardando(true);
    try {
      let guardado = await guardarProyecto(proyecto?.id, form);
      if (portadaNueva) {
        const imagen = portadaNueva.type ? portadaNueva : new Blob([portadaNueva], { type: "image/jpeg" });
        guardado = await cambiarPortada(guardado, imagen);
      } else if (quitarPortada) {
        guardado = await quitarPortadaDe(guardado);
      }
      onGuardado(guardado);
    } catch (error) {
      avisarError(error);
      setGuardando(false);
    }
  };

  return (
    <Ventana titulo={proyecto ? "Editar proyecto" : "Nuevo proyecto"} onCerrar={onCerrar}>
      <form onSubmit={guardar}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={etiqueta} htmlFor="proyecto-nombre">Nombre *</label>
            <input id="proyecto-nombre" className={campo} value={form.nombre} onChange={cambiar("nombre")} placeholder="Ej. Retiro de jóvenes" autoFocus />
          </div>
          <div className="sm:col-span-2">
            <label className={etiqueta} htmlFor="proyecto-descripcion">Descripción</label>
            <textarea id="proyecto-descripcion" className={`${campo} min-h-[90px]`} value={form.descripcion} onChange={cambiar("descripcion")} placeholder="Objetivo, alcance, notas…" />
          </div>
          <div className="sm:col-span-2">
            <span className={etiqueta}>Imagen de portada (opcional)</span>
            <div className="relative overflow-hidden rounded-xl border border-white/10 bg-zinc-900">
              {vistaPrevia && <img src={vistaPrevia} alt="Portada del proyecto" className="h-40 w-full object-cover" />}
              <div className={`flex flex-wrap items-center gap-3 p-3 ${vistaPrevia ? "absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent pt-10" : ""}`}>
                <button type="button" onClick={() => selector.current?.click()} disabled={preparando} className={`${botonFantasma} ${pequeno}`}>
                  {preparando ? "Preparando…" : vistaPrevia ? "Cambiar imagen" : "Subir imagen"}
                </button>
                <input ref={selector} type="file" accept="image/*" className="hidden" onChange={(e) => { elegirImagen(e.target.files[0]); e.target.value = ""; }} />
                {vistaPrevia ? (
                  <button type="button" onClick={quitar} className="text-[10px] font-bold uppercase tracking-widest text-gray-300 hover:text-rose-400">Quitar imagen</button>
                ) : (
                  <span className="text-[11px] text-gray-500">Se muestra en la tarjeta y en el encabezado del proyecto.</span>
                )}
              </div>
            </div>
          </div>
          <div>
            <label className={etiqueta} htmlFor="proyecto-ministerio">Ministerio / área</label>
            <input id="proyecto-ministerio" className={campo} value={form.ministerio} onChange={cambiar("ministerio")} placeholder="Ej. Jóvenes" />
          </div>
          <div>
            <label className={etiqueta} htmlFor="proyecto-lider">Líder del proyecto</label>
            <SelectorPersona
              id="proyecto-lider"
              personas={personas}
              valor={form.lider_id}
              onCambiar={(id) => setForm({ ...form, lider_id: id })}
              vacio="Sin líder"
              deshabilitado={!puedeCambiarLider}
            />
          </div>
          <div>
            <label className={etiqueta} htmlFor="proyecto-estado">Estado</label>
            <Selector id="proyecto-estado" className={campo} value={form.estado} onChange={cambiar("estado")}>
              {ESTADOS_PROYECTO.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}
            </Selector>
          </div>
          <div>
            <label className={etiqueta} htmlFor="proyecto-prioridad">Prioridad</label>
            <Selector id="proyecto-prioridad" className={campo} value={form.prioridad} onChange={cambiar("prioridad")}>
              {PRIORIDADES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </Selector>
          </div>
          <div>
            <label className={etiqueta} htmlFor="proyecto-inicio">Fecha de inicio</label>
            <input id="proyecto-inicio" type="date" className={campo} value={form.fecha_inicio} onChange={cambiar("fecha_inicio")} />
          </div>
          <div>
            <label className={etiqueta} htmlFor="proyecto-fin">Fecha de fin</label>
            <input id="proyecto-fin" type="date" className={campo} value={form.fecha_fin} min={form.fecha_inicio || undefined} onChange={cambiar("fecha_fin")} />
          </div>
        </div>
        <div className="mt-8 flex flex-wrap justify-end gap-2">
          <button type="button" onClick={onCerrar} className={botonFantasma}>Cancelar</button>
          <button type="submit" disabled={guardando || preparando} className={botonPrimario}>{guardando ? "Guardando…" : "Guardar"}</button>
        </div>
      </form>
    </Ventana>
  );
}

export default FormProyecto;
